"use client";
import * as React from "react";
import { cn } from "../lib/cn";

/**
 * Card — white surface, 1px hairline border, 12px radius, NO resting shadow.
 * Set `interactive` for the warm hover-lift (use on clickable cards only).
 *
 *   <Card><CardHeader><CardTitle>Funds</CardTitle></CardHeader><CardBody>…</CardBody></Card>
 *   <Card interactive>…launcher tile…</Card>
 */
export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  interactive?: boolean;
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ interactive, className, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn(
        "bg-white rounded-[var(--ri-radius-card)] border border-[var(--ri-hairline)]",
        interactive &&
          "transition-[box-shadow,transform,border-color] duration-300 ease-[var(--ri-ease)] " +
            "hover:-translate-y-0.5 hover:shadow-[var(--ri-shadow-lift)] hover:border-[var(--ri-sage)] cursor-pointer",
        className,
      )}
      {...rest}
    />
  ),
);
Card.displayName = "Card";

export function CardHeader({ className, ...rest }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("p-5 pb-0", className)} {...rest} />;
}

export function CardTitle({ className, ...rest }: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3
      className={cn("text-[15px] font-bold text-[var(--ri-ink)] [font-family:var(--ri-font-sans)]", className)}
      {...rest}
    />
  );
}

export function CardBody({ className, ...rest }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("p-5", className)} {...rest} />;
}
